import { Checkbox } from "antd";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import InputSearch from "../../../../../../components/Basic/InputSearch";
import Checklist from '../../../../../../components/Checklist';
import Icon, { ICON_NAME } from "../../../../../../components/Icon";
import LoadingBox from "../../../../../../components/LoadingBox";
import Menu from "../../../../../../components/Menu";
import { openPopup2 } from "../../../../../../contexts/PopupContext";
import { selectChecklist } from "../../../../../../util/store/checklist/constants";
import { useSelector } from "../../../../../../util/store/store";
import { ACTION_TYPE, OPERATION_TYPE, TASK_TYPE, TREATMENT_TYPE, vesselType, zIndex } from "../../../../../../util/varibles/constants";
import { cloneObj } from "../../../../../../util/varibles/global";
import { IChecklist, ICombineTreatment, ISorting, TOperation } from "../../../../../../util/varibles/interface";
import { Names } from "../../../../../ChecklistPage/Checklists";
import { getEditOp } from "../../EditOperationContext";
import styles from './style.module.scss';

const conditionOfOp: { [id: string]: (data: Partial<TOperation>) => string[] } = {
    [OPERATION_TYPE.HARVEST]: () => [`operation_type#${OPERATION_TYPE.HARVEST}`],
    [OPERATION_TYPE.TRANSPORT]: () => [`operation_type#${OPERATION_TYPE.TRANSPORT}`],
    [OPERATION_TYPE.EVENT]: ({ event_type }) => [`operation_type#${OPERATION_TYPE.EVENT}#${event_type}`],
    [OPERATION_TYPE.TREATMENT]: ({ tasks = [] }) => tasks.reduce((rs: string[], item) => {
        const { type, treatment_type } = item;

        if (type === TASK_TYPE.COMBINE) {
            const { sub_tasks } = item as ICombineTreatment;
            sub_tasks.forEach(sub => {
                rs.push(`operation_type#${OPERATION_TYPE.TREATMENT}#${sub.treatment_type}`);
            })
        } else if (treatment_type === TREATMENT_TYPE.SORTING) {
            const { sub_tasks } = item as ISorting;
            Object.keys(sub_tasks).forEach((key: string) => {
                rs.push(`operation_type#${OPERATION_TYPE.TREATMENT}#${TREATMENT_TYPE.SORTING}`);
                sub_tasks[key].forEach((task) => {
                    if (typeof task.treatment_type === "number")
                        rs.push(`operation_type#${OPERATION_TYPE.TREATMENT}#${task.treatment_type}`);
                    else if (task.type === TASK_TYPE.COMBINE) {
                        const { sub_tasks: lastSubTasks } = item as ICombineTreatment;
                        lastSubTasks.forEach(sub => {
                            rs.push(`operation_type#${OPERATION_TYPE.TREATMENT}#${sub.treatment_type}`);
                        })
                    }
                });
            });
        } else if (treatment_type !== undefined) {
            rs.push(`operation_type#${OPERATION_TYPE.TREATMENT}#${treatment_type}`)
        }
        return rs;
    }, [])
}

interface IProps {
    data: TOperation
    isContent?: boolean
    disabled?: boolean

    onChange(value: TOperation['checklist_ids']): void
}

interface IAssignChecklist extends IProps {
    checklists: IChecklist[]
}

const AssignChecklist: React.FC<IAssignChecklist> = ({ data, checklists, onChange }) => {
    const loading = useSelector(selectChecklist.loading);
    const { checklist_ids: ids = [] } = data;
    const checklist_ids = new Set(ids);
    const [search, setSearch] = useState("");

    const handleChange = (checked: boolean, id: string) => {
        if (checked) {
            onChange([...data.checklist_ids || [], id]);
        } else {
            onChange((data.checklist_ids || []).filter(item => item !== id));
        }
    }

    return <div className='max-w-[420px] w-full'>
        <div className='font-[600] text-[16px] mt-2 mb-1'>Add Checklist</div>
        <InputSearch className="mb-2 mr-2" value={search} placeHolder="Search for checklist"
            onChange={value => setSearch(value)} />
        {checklists.length === 0 && <LoadingBox loading={loading} />}
        <div className='flex flex-col pb-1 max-h-[calc(100vh-110px)] overflow-auto pr-2'>
            {checklists.map(item => {
                const isSearch = search.length === 0 || item.name?.toLowerCase().includes(search.toLowerCase());

                if (!isSearch)
                    return null;

                const checked = checklist_ids.has(item.id || "");

                return <Checkbox key={item.id} className='h-[36px] flex items-center' checked={checked} onChange={() => handleChange(!checked, item.id || "")}>
                    {item.name}
                </Checkbox>
            })}
        </div>
    </div>
}

export const ChecklistNames: React.FC<{ checklist_ids?: string[], onClear(id: string): void }> = ({ checklist_ids = [], onClear }) => {
    const checklists = useSelector(selectChecklist.data);

    const openChecklist = useCallback((checklist: IChecklist) => {
        openPopup2(<Checklist checklist={checklist} />, {
            modalProps: {
                title: checklist.name,
                width: '860px',
                zIndex: zIndex.popupLev2
            }
        })
    }, [])

    return (
        <>
            {checklist_ids.map(id => {
                const item = checklists.find(item => item.id === id);
                if (!item)
                    return null;

                return (
                    <div className={styles.checklist} key={id}>
                        <span className='link' onClick={() => openChecklist(item)}>{item.name}</span>
                        <div data-role='close'>
                            <Icon
                                className='mt-[1px]'
                                icon={ICON_NAME.CLOSE}
                                bg='colorLink'
                                onClick={() => onClear(id)}
                            />
                        </div>
                    </div>
                );
            })}
        </>
    );
}

const AddChecklist: React.FC<IProps> = ({ data, isContent = true, onChange }) => {
    const checklists = useSelector(selectChecklist.data);
    const { checklist_ids = [] } = data;
    const get = useRef(getEditOp());
    const checklistContainerRef = useRef<HTMLDivElement>(null);
    const [scrollState, setScrollState] = useState({ hasOverflow: false, canScrollLeft: false, canScrollRight: false });

    const updateScrollState = useCallback(() => {
        const container = checklistContainerRef.current;
        if (!container)
            return;

        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        setScrollState({
            hasOverflow: maxScrollLeft > 1,
            canScrollLeft: container.scrollLeft > 1,
            canScrollRight: maxScrollLeft - container.scrollLeft > 1
        });
    }, []);

    useEffect(() => {
        const container = checklistContainerRef.current;
        if (!container)
            return;

        const resizeObserver = new ResizeObserver(updateScrollState);
        const handleWheel = (event: WheelEvent) => {
            if (container.scrollWidth - container.clientWidth <= 1)
                return;

            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (delta === 0)
                return;

            event.preventDefault();
            event.stopPropagation();
            container.scrollLeft += delta;
        }

        resizeObserver.observe(container);
        Array.from(container.children).forEach(child => resizeObserver.observe(child));
        container.addEventListener('wheel', handleWheel, { passive: false });
        updateScrollState();

        return () => {
            resizeObserver.disconnect();
            container.removeEventListener('wheel', handleWheel);
        };
    }, [checklist_ids, checklists, updateScrollState]);

    const scrollChecklists = (direction: -1 | 1) => {
        checklistContainerRef.current?.scrollBy({
            left: direction * Math.max(160, checklistContainerRef.current.clientWidth * .6),
            behavior: 'smooth'
        });
    }

    useEffect(() => {
        const { editMode, vessel, operation, opType = "" } = get.current();
        if (editMode === ACTION_TYPE.CREATE && vessel && operation) {
            const { id, name, type } = vessel;
            const suggest = checklists.reduce((rs: { [id: string]: { nameSuggest: string[], sub_types?: string[] } }, item) => {
                const { vessel_types = [], vessel_names = [], sub_types } = item.required_map || {};
                let count = 0;
                if (vessel_types.length > 0) {
                    if (new Set(vessel_types).has((type || "").toString()))
                        rs[item.id || ""] = { nameSuggest: [vesselType[type || ""]?.name], sub_types }
                } else {
                    count++;
                }

                if (vessel_names.length > 0) {
                    if (new Set(vessel_names.map(sub => sub.id)).has((id || "").toString())) {
                        if (rs[item.id || ""])
                            rs[item.id || ""].nameSuggest.push(name || "")
                        else {
                            rs[item.id || ""] = { nameSuggest: [name || ""], sub_types }
                        }
                    }
                } else {
                    count++;
                }

                if (count === 2) {
                    rs[item.id || ""] = { nameSuggest: [], sub_types }
                }

                return rs;
            }, {});
            const { event_type, tasks } = operation;
            const { [opType]: getCondition } = conditionOfOp;
            const condition = getCondition ? new Set(getCondition({ event_type, tasks })) : new Set([]);
            const filter = Object.keys(suggest).reduce((rs: {
                [id: string]: string[]
            }, key) => {
                const { sub_types = [], nameSuggest } = suggest[key] || {};
                if (sub_types.length > 0) {
                    const result: string[] = cloneObj(nameSuggest);
                    sub_types.forEach(id => {
                        if (condition.has(id)) {
                            const [, opType, taskType, treatmentType] = id.split('#');
                            const { [opType]: Name } = Names;
                            if (Name)
                                result.push(Name?.({ data: { opType, taskType, treatmentType, id } }))
                        }

                    });
                    if (result.length > 0) {
                        rs[key] = result;
                    }
                }
                return rs;
            }, {});
            onChange(Object.keys(filter));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <div className={`flex min-w-0 gap-3 items-center ${isContent ? 'w-full' : ''}`}>
        {isContent && checklist_ids.length > 0 && <div
            className={styles.checklistViewport}
            data-overflow={scrollState.hasOverflow}
            data-scroll-left={scrollState.canScrollLeft}
            data-scroll-right={scrollState.canScrollRight}
        >
            <div ref={checklistContainerRef} className={styles.checklistScroll} onScroll={updateScrollState}>
                <ChecklistNames checklist_ids={checklist_ids} onClear={id => onChange(checklist_ids.filter(item => item !== id))} />
            </div>
            <button
                type='button'
                className={styles.scrollLeft}
                aria-label='Scroll checklists left'
                onClick={() => scrollChecklists(-1)}
            >
                <Icon icon={ICON_NAME.ARROW_RIGHT_3} size={10} bg='colorLink' className='rotate-180' />
            </button>
            <button
                type='button'
                className={styles.scrollRight}
                aria-label='Scroll checklists right'
                onClick={() => scrollChecklists(1)}
            >
                <Icon icon={ICON_NAME.ARROW_RIGHT_3} size={10} bg='colorLink' />
            </button>
        </div>}
        <Menu
            items={[
                {
                    key: 'checklists',
                    disabled: true,
                    className: styles.assign,
                    label: <AssignChecklist data={data} onChange={onChange} checklists={checklists} />
                }
            ]}
            placement='bottom'
        >
            <div className='bt-ghost shrink-0 min-w-[112px!important]' data-icon='true'>
                <div className='flex items-center gap-2 px-3'>
                    <span className='whitespace-nowrap text-[15px]'>
                        Add checklist{scrollState.hasOverflow ? ` (${checklist_ids.length})` : ''}
                    </span>
                    <Icon className='mb-[2px]' icon={ICON_NAME.ARROW_DOWN_3} size='small' />
                </div>
            </div>
        </Menu>
    </div>;
};

export default AddChecklist;
